import { ChevronLeft, ChevronRight, HeartPulse, Pencil } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useDDSEmotions } from '@/api/queries/dds-emotions-queries'
import {
	DDS_MOOD_STYLES,
	DdsEmotionAccessInfo,
	DdsMoodActionDialog,
	type DdsMoodActionTarget,
	DdsMoodIcon,
} from '@/components/dds-attendance'
import {
	INSPECTION_COLORS,
	InspectionCard,
	InspectionCardHeader,
} from '@/components/inspections'
import { Button } from '@/components/ui/button'
import { CardContent, CardFooter } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { Skeleton } from '@/components/ui/skeleton'
import { ROUTES } from '@/constants/routes'
import { cn } from '@/lib/utils'
import { useProfileStore } from '@/store'
import { DDS_MOODS, type DDSEmotionEntry, type DDSMood } from '@/types'
import { canRegisterDdsMoodAction } from '@/utils/dds-attendance'
import { formatDateToDisplay } from '@/utils/formatDate'

type ActionFilter = 'all' | 'pending' | 'done'

const ACTION_FILTER_VALUES: Record<ActionFilter, boolean | undefined> = {
	all: undefined,
	pending: false,
	done: true,
}

const formatDateTime = (iso: string) =>
	new Date(iso).toLocaleString('pt-BR', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	})

export const DDSEmotionsPage = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { defaultBranchId } = useProfileStore()

	const [mood, setMood] = useState<DDSMood | undefined>()
	const [actionFilter, setActionFilter] = useState<ActionFilter>('all')
	const [startDate, setStartDate] = useState('')
	const [endDate, setEndDate] = useState('')
	const [page, setPage] = useState(1)
	const [actionTarget, setActionTarget] = useState<DdsMoodActionTarget | null>(
		null,
	)

	const { data, isLoading, isError } = useDDSEmotions({
		mood,
		hasAction: ACTION_FILTER_VALUES[actionFilter],
		startDate: startDate || undefined,
		endDate: endDate || undefined,
		branchId: defaultBranchId || undefined,
		page,
	})

	const items = data?.items ?? []
	const totalPages = data?.pagination.totalPages ?? 1
	const summaryTotal = data
		? DDS_MOODS.reduce((sum, key) => sum + data.moodSummary[key], 0)
		: 0

	// qualquer filtro novo volta para a primeira página
	const resetPage = () => setPage(1)

	const openAction = (entry: DDSEmotionEntry) =>
		setActionTarget({
			ddsId: entry.ddsId,
			attendeeId: entry.attendeeId,
			personName: entry.personName,
			mood: entry.mood,
			moodAction: entry.moodAction,
		})

	return (
		<InspectionCard colors={INSPECTION_COLORS.honeyYellow}>
			<InspectionCardHeader
				actions={<DdsEmotionAccessInfo />}
				colors={INSPECTION_COLORS.honeyYellow}
				icon={<HeartPulse className="size-5" />}
				onBack={() => navigate(ROUTES.DDS.HUB)}
				subtitle={t('ddsEmotions.subtitle')}
				title={t('ddsEmotions.title')}
			/>

			<CardContent className="flex-1 space-y-4">
				<div
					aria-label={t('ddsEmotions.filterByMood')}
					className="flex flex-wrap gap-2"
					role="group"
				>
					<Button
						aria-pressed={!mood}
						className={cn(
							'w-auto gap-2',
							!mood &&
								'border-kaefer-dark-grey bg-kaefer-dark-grey text-white hover:bg-kaefer-dark-grey/90 hover:text-white',
						)}
						onClick={() => {
							setMood(undefined)
							resetPage()
						}}
						variant="outline"
					>
						{t('ddsEmotions.allMoods')}
						<span className="tabular-nums">{summaryTotal}</span>
					</Button>
					{DDS_MOODS.map((key) => (
						<Button
							aria-pressed={mood === key}
							className={cn(
								'w-auto gap-2',
								mood === key
									? DDS_MOOD_STYLES[key].selected
									: DDS_MOOD_STYLES[key].idle,
							)}
							key={key}
							onClick={() => {
								setMood(mood === key ? undefined : key)
								resetPage()
							}}
							variant="outline"
						>
							<DdsMoodIcon
								className={cn('size-5', mood === key && 'text-current')}
								mood={key}
							/>
							{t(`ddsMood.${key}`)}
							<span className="tabular-nums">
								{data?.moodSummary[key] ?? 0}
							</span>
						</Button>
					))}
				</div>

				<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
					<label className="flex flex-col gap-1.5 text-kaefer-medium-grey text-xs font-semibold">
						{t('ddsEmotions.actionStatus')}
						<NativeSelect
							onChange={(event) => {
								setActionFilter(event.target.value as ActionFilter)
								resetPage()
							}}
							value={actionFilter}
						>
							<NativeSelectOption value="all">
								{t('ddsEmotions.actionFilter.all')}
							</NativeSelectOption>
							<NativeSelectOption value="pending">
								{t('ddsEmotions.actionFilter.pending')}
							</NativeSelectOption>
							<NativeSelectOption value="done">
								{t('ddsEmotions.actionFilter.done')}
							</NativeSelectOption>
						</NativeSelect>
					</label>
					<label className="flex flex-col gap-1.5 text-kaefer-medium-grey text-xs font-semibold">
						{t('ddsEmotions.startDate')}
						<Input
							max={endDate || undefined}
							onChange={(event) => {
								setStartDate(event.target.value)
								resetPage()
							}}
							type="date"
							value={startDate}
						/>
					</label>
					<label className="flex flex-col gap-1.5 text-kaefer-medium-grey text-xs font-semibold">
						{t('ddsEmotions.endDate')}
						<Input
							min={startDate || undefined}
							onChange={(event) => {
								setEndDate(event.target.value)
								resetPage()
							}}
							type="date"
							value={endDate}
						/>
					</label>
				</div>

				{isLoading ? (
					<div className="space-y-3">
						{[0, 1, 2].map((key) => (
							<Skeleton className="h-28 w-full rounded-xl" key={key} />
						))}
					</div>
				) : isError ? (
					<p className="py-10 text-center text-destructive text-sm">
						{t('ddsEmotions.loadError')}
					</p>
				) : items.length === 0 ? (
					<div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-kaefer-light-silver py-16">
						<HeartPulse className="size-10 text-kaefer-silver" />
						<p className="mt-3 text-muted-foreground text-sm">
							{t('ddsEmotions.empty')}
						</p>
					</div>
				) : (
					<ul className="grid grid-cols-1 gap-3 lg:grid-cols-2">
						{items.map((entry) => (
							<li
								className="flex flex-col gap-3 rounded-xl border bg-card p-4"
								key={entry.attendeeId}
							>
								<div className="flex items-start justify-between gap-3">
									<div className="min-w-0">
										<p className="truncate font-semibold text-kaefer-dark-grey text-sm">
											{entry.personName}
										</p>
										<p className="text-muted-foreground text-xs">
											{entry.badge}
											{entry.jobRole ? ` · ${entry.jobRole}` : ''}
										</p>
									</div>
									<span
										className={cn(
											'flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 font-semibold text-xs',
											DDS_MOOD_STYLES[entry.mood].idle,
										)}
									>
										<DdsMoodIcon className="size-4" mood={entry.mood} />
										{t(`ddsMood.${entry.mood}`)}
									</span>
								</div>

								<button
									className="text-left text-muted-foreground text-xs hover:underline"
									onClick={() =>
										navigate(ROUTES.DDS.DETAIL.replace(':id', entry.ddsId))
									}
									type="button"
								>
									{entry.ddsSubject} · {formatDateToDisplay(entry.ddsDate)} ·{' '}
									{entry.crName}
								</button>

								<div className="rounded-lg bg-muted/30 px-3 py-2 text-sm">
									{entry.moodAction ? (
										<>
											<p className="whitespace-pre-wrap text-kaefer-dark-grey">
												{entry.moodAction}
											</p>
											{entry.moodActionAt && (
												<p className="mt-1 text-muted-foreground text-xs">
													{t('ddsEmotions.actionBy', {
														name: entry.moodActionByName ?? '-',
														date: formatDateTime(entry.moodActionAt),
													})}
												</p>
											)}
										</>
									) : (
										<p className="text-muted-foreground">
											{t('ddsEmotions.noAction')}
										</p>
									)}
								</div>

								{canRegisterDdsMoodAction(entry.mood) && (
									<Button
										className="w-full gap-2 sm:w-auto sm:self-end"
										onClick={() => openAction(entry)}
										size="sm"
										variant="outline"
									>
										<Pencil className="size-4" />
										{entry.moodAction
											? t('ddsEmotions.editAction')
											: t('ddsEmotions.registerAction')}
									</Button>
								)}
							</li>
						))}
					</ul>
				)}

				{totalPages > 1 && (
					<div className="flex items-center justify-center gap-3">
						<Button
							aria-label={t('ddsEmotions.previousPage')}
							disabled={page <= 1}
							onClick={() => setPage((current) => current - 1)}
							size="icon"
							variant="outline"
						>
							<ChevronLeft className="size-4" />
						</Button>
						<span className="text-muted-foreground text-sm tabular-nums">
							{t('ddsEmotions.pageOf', { page, total: totalPages })}
						</span>
						<Button
							aria-label={t('ddsEmotions.nextPage')}
							disabled={page >= totalPages}
							onClick={() => setPage((current) => current + 1)}
							size="icon"
							variant="outline"
						>
							<ChevronRight className="size-4" />
						</Button>
					</div>
				)}
			</CardContent>

			<CardFooter className="mt-4 border-t border-kaefer-honey-yellow/30 bg-kaefer-honey-yellow/5 py-3">
				<p className="w-full text-muted-foreground text-xs">
					{t('ddsEmotions.footer', { count: data?.pagination.total ?? 0 })}
				</p>
			</CardFooter>

			<DdsMoodActionDialog
				onClose={() => setActionTarget(null)}
				target={actionTarget}
			/>
		</InspectionCard>
	)
}
